/**
 * DTemplate Solution Slide
 * One platform under a mom's many problems, and the loop each of them runs:
 * her wish improves the data, models and capabilities the next mom starts from
 */

import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { Baby, CalendarDots, Car, ChartLineUp, Heartbeat, Tent, Tree, TShirt, type Icon } from '@phosphor-icons/react';
import { ENGINE_KINDS, ENGINE_ORDER, EngineIcon, type EngineKey } from '@components/wish-story/engines';
import { EASE_OUT_EXPO } from '@components/motion/scroll-motion';
import { DECK_DEMOS } from '@components/press/pitch-deck/slides/dtemplate/demos';
import { StatementSlide, useArrive, useStill } from '@components/press/pitch-deck/slides/dtemplate/frame';
import { colors } from '@config/colors';
import { typography } from '@config/typography';

/*
 * June, 2026-10-10: "The solution slide needs to be so much more advanced
 * … sell the message of a platform software for solving a variety of
 * problems, where each of those problems improves with the loop of wish ->
 * {data, models, capabilities} -> next mom. A good visual guide will be
 * key here." Her lines of 10-09 stay, set into the picture: new to her,
 * not to us (the wish); kindness and thoughtfulness (the platform's own
 * line); the vision of the hundredth mom (under it all).
 *
 * The problems stand on the platform as apps do: the four live in the app
 * (their wishes the app's own words, from `DECK_DEMOS`), then the four
 * coming soon. The platform holds the loop, read left to right — ① a
 * mom's wish, ② what every wish improves, ③ the next mom — and back round
 * underneath to the next wish.
 *
 * The guide is one live problem taken round the loop: its tile marked, its
 * wish in ①, what its wishes built under each kind in ② (the Product
 * slide's lines), and what the next mom gets in ③ (the product pages'
 * taglines, in the third person; Kids Clothes' from its recording). Moving,
 * the example turns through the four, a wish going once round the loop for
 * each; still — the export — it is Adventures.
 */

type Problem = { key: string; name: string; wish: string; icon: Icon };
type LiveProblem = Problem & { built: Record<EngineKey, string>; gets: string };

const LIVE_ICONS: Readonly<Record<string, Icon>> = {
  adventures: Tree,
  growth: ChartLineUp,
  commute: Car,
  'kids-clothes': TShirt,
};

/** What the next mom gets, from each product's own page. */
const GETS: Readonly<Record<string, string>> = {
  adventures: 'A whole Saturday, planned around her kids',
  growth: 'Her family’s growth story, on the charts pediatricians use',
  commute: 'Every way to work, compared in miles, minutes and dollars',
  'kids-clothes': 'Each child’s size at every brand, and the sales on now',
};

const LIVE: readonly LiveProblem[] = DECK_DEMOS.map((demo) => ({
  key: demo.key,
  name: demo.title,
  wish: demo.wish,
  icon: LIVE_ICONS[demo.key] ?? Tree,
  built: demo.built,
  gets: GETS[demo.key] ?? '',
}));

const COMING: readonly Problem[] = [
  { key: 'childcare', name: 'Push Button Childcare', wish: 'Who can watch the kids on a sick day?', icon: Baby },
  { key: 'school', name: 'School Calendar', wish: 'When are the kids off school?', icon: CalendarDots },
  { key: 'camps', name: 'Camps and birthday hosts', wish: 'Which camp this summer?', icon: Tent },
  { key: 'ivf', name: 'IVF', wish: 'What are our chances with IVF?', icon: Heartbeat },
];

/** The kinds in the deck's sentence case. */
const KIND_LINES: Record<EngineKey, string> = {
  data: 'Pre-fetched data',
  expert: 'Domain expert models',
  skills: 'Assistant capabilities',
};

/** How long each example takes: one wish, once round the loop. */
const LAP_MS = 5600;
/** When the first lap starts: once the picture has arrived. */
const FIRST_LAP_MS = 2800;

// ── Geometry, on the body's 1640px ───────────────────────────────────────────

const W = 1640;
const H = 580;

/** The problems: eight across, the coming four a step apart from the live. */
const TILE_W = 190;
const TILE_GAP = 14;
const GROUP_GAP = 20;
const TILE_TOP = 30;
const TILE_H = 136;
const tileLeft = (i: number) => i * (TILE_W + TILE_GAP) + (i >= 4 ? GROUP_GAP : 0);

/** The platform they stand on. */
const SLAB_TOP = TILE_TOP + TILE_H + 12;
const SLAB_H = 334;

/** The loop's track inside it: a stadium, the stations on its top
 *  straight, the way back along the bottom. */
const T = { left: 70, right: W - 70, top: SLAB_TOP + 152, bottom: SLAB_TOP + 290 };
const TR = (T.bottom - T.top) / 2;
const TRACK = `M ${T.left + TR} ${T.top} H ${T.right - TR} A ${TR} ${TR} 0 0 1 ${T.right - TR} ${T.bottom} H ${T.left + TR} A ${TR} ${TR} 0 0 1 ${T.left + TR} ${T.top} Z`;

/** The stations, centred on the top straight. */
const WISH = { x: 282, w: 314, h: 150 };
const KINDS = { x: W / 2, w: 664, h: 180 };
const NEXT = { x: W - 282, w: 314, h: 150 };

/** Arrowheads: [x, y, degrees], pointing the way the loop turns. */
const TURNS: ReadonlyArray<[number, number, number]> = [
  [(WISH.x + WISH.w / 2 + KINDS.x - KINDS.w / 2) / 2 + 10, T.top, 0],
  [(KINDS.x + KINDS.w / 2 + NEXT.x - NEXT.w / 2) / 2 + 10, T.top, 0],
  [T.right, (T.top + T.bottom) / 2, 90],
  [W / 2 + 330, T.bottom, 180],
  [W / 2 - 330, T.bottom, 180],
  [T.left, (T.top + T.bottom) / 2, 270],
];

/** Named for the first of the conclusion's three loops (June, 2026-10-14). */
export const DSolutionSlide: React.FC = () => (
  <StatementSlide
    section="Solution · the product loop"
    question="What’s the secret?"
    headline="One platform for a mom’s many problems. Every wish makes each one better for the next mom."
  >
    <PlatformLoop />
  </StatementSlide>
);

/** The lap the example is on, counting from 0 once the picture has
 *  arrived; still, always the first. */
function useLap(still: boolean) {
  const [running, setRunning] = useState(false);
  const [lap, setLap] = useState(0);
  useEffect(() => {
    if (still) return undefined;
    const start = window.setTimeout(() => setRunning(true), FIRST_LAP_MS);
    return () => window.clearTimeout(start);
  }, [still]);
  useEffect(() => {
    if (!running) return undefined;
    const turn = window.setInterval(() => setLap((n) => n + 1), LAP_MS);
    return () => window.clearInterval(turn);
  }, [running]);
  return { running, lap };
}

const PlatformLoop: React.FC = () => {
  const arrive = useArrive();
  const still = useStill();
  const { running, lap } = useLap(still);
  const active = lap % LIVE.length;
  const example = LIVE[active];
  const fade = (i: number) => ({
    initial: still ? (false as const) : { opacity: 0 },
    animate: { opacity: 1 },
    transition: { duration: 0.7, ease: EASE_OUT_EXPO, delay: 0.2 + i * 0.12 },
  });
  /** A line that changes with the example, fading in as it does. */
  const swap = {
    initial: still ? (false as const) : { opacity: 0, y: 6 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.45, ease: EASE_OUT_EXPO },
  };
  const problems: readonly Problem[] = [...LIVE, ...COMING];
  return (
    <Stage style={{ width: W, height: H }}>
      <GroupLabel style={{ left: 0, width: tileLeft(3) + TILE_W }} {...arrive(0, { opacity: 0 })}>
        Live in the app
      </GroupLabel>
      <GroupLabel style={{ left: tileLeft(4), width: tileLeft(7) + TILE_W - tileLeft(4) }} {...arrive(4, { opacity: 0 })}>
        Coming soon
      </GroupLabel>
      {problems.map((problem, i) => (
        <ProblemTile
          key={problem.key}
          problem={problem}
          left={tileLeft(i)}
          coming={i >= LIVE.length}
          example={i === active}
          arrival={arrive(1 + i)}
        />
      ))}

      <Slab style={{ top: SLAB_TOP, height: SLAB_H }} {...arrive(9, { opacity: 0, y: 24 })}>
        <SlabLabel>The Home CEO platform</SlabLabel>
        <SlabCreed>Every answer led with kindness and thoughtfulness</SlabCreed>
      </Slab>

      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
        {/* Each problem plugged into the platform; the example's lit. */}
        <motion.g {...fade(10)}>
          {problems.map((problem, i) => {
            const lit = i === active;
            const coming = i >= LIVE.length;
            return (
              <path
                key={problem.key}
                d={`M ${tileLeft(i) + TILE_W / 2} ${TILE_TOP + TILE_H} V ${SLAB_TOP + 22}`}
                stroke={lit ? colors.accent.coral.extraDark : coming ? colors.brand.extraDark : colors.brand.exxtraDark}
                strokeWidth={lit ? 6 : 4}
                strokeLinecap="round"
                strokeDasharray={coming ? '4 6' : undefined}
                fill="none"
              />
            );
          })}
        </motion.g>
        {/* The loop, drawn round from the wish, then its dashes flowing. */}
        <motion.path
          d={TRACK}
          fill="none"
          stroke={colors.brand.exxtraDark}
          strokeWidth="5"
          initial={still ? false : { pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1.6, ease: EASE_OUT_EXPO, delay: 1.5 }}
        />
        <motion.g {...fade(18)}>
          <Track d={TRACK} fill="none" stroke={colors.brand.extraDark} strokeWidth="5" $still={still} />
        </motion.g>
        {TURNS.map(([x, y, deg]) => (
          <motion.path
            key={`${x}-${y}`}
            d="M -12 -11 L 12 0 L -12 11 Z"
            fill={colors.accent.coral.extraDark}
            transform={`translate(${x} ${y}) rotate(${deg})`}
            {...fade(16)}
          />
        ))}
      </svg>

      {/* The example's wish, once round the loop. */}
      {running && <Wish key={lap} aria-hidden="true" />}

      <Station style={{ left: WISH.x - WISH.w / 2, top: T.top - WISH.h / 2, width: WISH.w, height: WISH.h }} {...arrive(12, { opacity: 0, scale: 0.92 })}>
        <StationHead>
          <Step>1</Step>
          <StationName>A mom’s wish</StationName>
        </StationHead>
        <motion.div key={example.key} {...swap}>
          <WishQuote>“{example.wish}”</WishQuote>
        </motion.div>
        <StationNote>New to her, not to us: many moms have had it</StationNote>
      </Station>

      <Station style={{ left: KINDS.x - KINDS.w / 2, top: T.top - KINDS.h / 2, width: KINDS.w, height: KINDS.h }} {...arrive(13, { opacity: 0, scale: 0.92 })}>
        <StationHead>
          <Step>2</Step>
          <StationName>Every wish improves</StationName>
          <ExampleTag>{example.name}</ExampleTag>
        </StationHead>
        <KindsRow>
          {ENGINE_ORDER.map((kind) => (
            <Kind key={kind}>
              <KindDisc style={{ background: ENGINE_KINDS[kind].fill }}>
                <EngineIcon engine={kind} size={19} color={ENGINE_KINDS[kind].iconInk} />
              </KindDisc>
              <div>
                <KindName>{KIND_LINES[kind]}</KindName>
                <motion.div key={example.key} {...swap}>
                  <KindBuilt>{example.built[kind]}</KindBuilt>
                </motion.div>
              </div>
            </Kind>
          ))}
        </KindsRow>
      </Station>

      <Station
        $next
        style={{ left: NEXT.x - NEXT.w / 2, top: T.top - NEXT.h / 2, width: NEXT.w, height: NEXT.h }}
        {...arrive(14, { opacity: 0, scale: 0.92 })}
      >
        <StationHead>
          <Step $next>3</Step>
          <StationName $light>The next mom</StationName>
        </StationHead>
        <motion.div key={example.key} {...swap}>
          <NextGets>{example.gets}</NextGets>
        </motion.div>
        <StationNote $light>Starts from every wish before hers</StationNote>
      </Station>

      <ReturnLabel style={{ top: T.bottom - 15 }} {...arrive(15, { opacity: 0 })}>
        Her wish starts the loop again
      </ReturnLabel>

      <Vision style={{ top: SLAB_TOP + SLAB_H + 26 }} {...arrive(17, { opacity: 0, y: 12 })}>
        <small>The vision</small> When a mom brings a wish, we’ve already helped <b>100 moms</b> with it, and know exactly
        how to help her best.
      </Vision>
    </Stage>
  );
};

const ProblemTile: React.FC<{
  problem: Problem;
  left: number;
  coming: boolean;
  example: boolean;
  arrival: ReturnType<ReturnType<typeof useArrive>>;
}> = ({ problem, left, coming, example, arrival }) => {
  const Glyph = problem.icon;
  return (
    <Tile $coming={coming} $example={example} style={{ left, top: TILE_TOP, width: TILE_W, height: TILE_H }} {...arrival}>
      {example && <TileTag>For example</TileTag>}
      <TileHead>
        <TileIcon $coming={coming}>
          <Glyph size={22} weight="duotone" color={colors.brand.exxxtraDark} aria-hidden="true" />
        </TileIcon>
        <TileName>{problem.name}</TileName>
      </TileHead>
      <TileWish>“{problem.wish}”</TileWish>
    </Tile>
  );
};

// ── Styles ───────────────────────────────────────────────────────────────────

const flow = keyframes`
  to { stroke-dashoffset: -48; }
`;

const round = keyframes`
  from { offset-distance: 0%; }
  to { offset-distance: 100%; }
`;

const Stage = styled.div`
  position: relative;
  margin: 0 auto;

  > svg {
    position: absolute;
    inset: 0;
    overflow: visible;
  }
`;

const GroupLabel = styled(motion.div)`
  position: absolute;
  top: 0;
  font-family: ${typography.fontFamily.small};
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${colors.brand.exxxtraDark};
`;

const Tile = styled(motion.div)<{ $coming: boolean; $example: boolean }>`
  position: absolute;
  box-sizing: border-box;
  padding: 12px 14px;
  border-radius: 18px;
  background: ${(p) => (p.$coming ? `color-mix(in srgb, ${colors.white} 55%, transparent)` : colors.white)};
  border: ${(p) =>
    p.$example ? `3px solid ${colors.accent.coral.extraDark}` : p.$coming ? `2px dashed ${colors.brand.extraDark}` : `2px solid ${colors.white}`};
  box-shadow: ${(p) => (p.$coming ? 'none' : `0 10px 22px ${colors.shadowSoft}`)};
  transition: border-color 0.4s ease;
`;

/* "For example", on the example's top edge. */
const TileTag = styled.div`
  position: absolute;
  top: -12px;
  right: 12px;
  padding: 2px 10px;
  border-radius: 9999px;
  background: ${colors.accent.coral.extraDark};
  font-family: ${typography.fontFamily.small};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${colors.white};
`;

const TileHead = styled.div`
  display: grid;
  grid-template-columns: 38px 1fr;
  align-items: center;
  column-gap: 10px;
  min-height: 40px;
`;

const TileIcon = styled.div<{ $coming: boolean }>`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => (p.$coming ? colors.white : colors.brand.primary)};
  border: ${(p) => (p.$coming ? `2px dashed ${colors.brand.extraDark}` : 'none')};
`;

const TileName = styled.div`
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 17.5px;
  font-weight: 600;
  line-height: 1.2;
  color: ${colors.neutral.extraDark};
`;

const TileWish = styled.div`
  margin-top: 8px;
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 15px;
  line-height: 1.35;
  color: ${colors.neutral.darkMedium};
`;

/* The platform: the deep seafoam the deck's panels wear. */
const Slab = styled(motion.div)`
  position: absolute;
  left: 0;
  right: 0;
  border-radius: 30px;
  background: ${colors.brand.exxxxtraDark};
  box-shadow: 0 18px 38px ${colors.shadowHard};
`;

const SlabLabel = styled.div`
  position: absolute;
  left: 34px;
  top: 26px;
  font-family: ${typography.fontFamily.small};
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${colors.brand.extraDark};
`;

const SlabCreed = styled.div`
  position: absolute;
  right: 34px;
  top: 24px;
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 17px;
  font-style: italic;
  color: color-mix(in srgb, ${colors.white} 78%, transparent);
`;

/* The loop's track, its dashes flowing round while the slide moves. */
const Track = styled.path<{ $still: boolean }>`
  stroke-dasharray: 16 8;
  animation: ${(p) => (p.$still ? 'none' : flow)} 1.4s linear infinite;
`;

/* The example's wish going once round the track, under the stations.
   At the stage's corner, so the path's coordinates are the stage's. */
const Wish = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${colors.accent.coral.dark};
  box-shadow:
    0 0 0 6px color-mix(in srgb, ${colors.accent.coral.extraDark} 30%, transparent),
    0 0 18px ${colors.accent.coral.extraDark};
  offset-path: path('${TRACK}');
  offset-rotate: 0deg;
  animation: ${round} ${LAP_MS}ms linear forwards;
`;

const Station = styled(motion.div)<{ $next?: boolean }>`
  position: absolute;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 20px;
  border-radius: 22px;
  background: ${(p) => (p.$next ? colors.brand.exxtraDark : colors.white)};
  border: 3px solid ${(p) => (p.$next ? colors.brand.extraDark : colors.accent.coral.extraDark)};
  box-shadow: 0 12px 26px rgba(0, 0, 0, 0.18);
`;

const StationHead = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StationName = styled.div<{ $light?: boolean }>`
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 22px;
  font-weight: 600;
  line-height: 1.2;
  color: ${(p) => (p.$light ? colors.white : colors.neutral.extraDark)};
`;

const StationNote = styled.div<{ $light?: boolean }>`
  margin-top: 8px;
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 14.5px;
  line-height: 1.3;
  color: ${(p) => (p.$light ? `color-mix(in srgb, ${colors.white} 80%, transparent)` : colors.neutral.medium)};
`;

const WishQuote = styled.div`
  margin-top: 10px;
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 17px;
  font-style: italic;
  line-height: 1.35;
  color: ${colors.accent.coral.exxxtraDark};
`;

const NextGets = styled.div`
  margin-top: 10px;
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 17px;
  font-weight: 500;
  line-height: 1.35;
  color: ${colors.white};
`;

const Step = styled.div<{ $next?: boolean }>`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 17px;
  font-weight: 700;
  background: ${(p) => (p.$next ? colors.white : colors.accent.coral.extraDark)};
  color: ${(p) => (p.$next ? colors.brand.exxtraDark : colors.white)};
`;

/* Which product the kinds' lines are from. */
const ExampleTag = styled.span`
  margin-left: auto;
  padding: 3px 12px;
  border-radius: 9999px;
  background: ${colors.accent.coral.exxtraLight};
  border: 1px solid ${colors.accent.coral.light};
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 14px;
  font-weight: 500;
  color: ${colors.accent.coral.exxxtraDark};
`;

const KindsRow = styled.div`
  margin-top: 14px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
`;

const Kind = styled.div`
  display: grid;
  grid-template-columns: 34px 1fr;
  align-items: start;
  column-gap: 10px;
`;

const KindDisc = styled.span`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const KindName = styled.div`
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 16px;
  font-weight: 600;
  line-height: 1.2;
  color: ${colors.neutral.extraDark};
`;

const KindBuilt = styled.div`
  margin-top: 4px;
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 14px;
  line-height: 1.3;
  color: ${colors.neutral.darkMedium};
`;

/* Centred by its margins: the arrival's transform is motion's to set. */
const ReturnLabel = styled(motion.div)`
  position: absolute;
  left: 0;
  right: 0;
  width: max-content;
  margin: 0 auto;
  padding: 4px 18px;
  border-radius: 9999px;
  background: ${colors.brand.exxxxtraDark};
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 17px;
  color: color-mix(in srgb, ${colors.white} 88%, transparent);
  white-space: nowrap;
`;

const Vision = styled(motion.div)`
  position: absolute;
  left: 0;
  right: 0;
  text-align: center;
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 23px;
  line-height: 1.4;
  color: ${colors.neutral.extraDark};

  small {
    margin-right: 10px;
    font-family: ${typography.fontFamily.small};
    font-size: 16px;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: ${colors.accent.coral.exxxtraDark};
  }

  b {
    font-weight: 600;
    color: ${colors.accent.coral.exxxtraDark};
  }
`;
